import { useMemo } from 'react';
import { Route, Routes } from 'react-router-dom-latest';
import { Button, Divider, Link, Paper, SearchBar, Stack, Typography } from '@common-components';
import { usePagination, useSearchMarket } from 'hooks';
import { useSearchOrganizationDomain } from 'hooks/api/organization-domain';
import { messages } from 'i18n';
import { BROKER_NESTED_ROUTES } from 'broker/broker-routes';
import NoItemsFound from 'broker/components/common/NoItemsFound';
import Sort from 'broker/components/common/Sort';
import TablePagination from 'broker/components/common/TablePagination';
import CarrierDialog from 'broker/dialogs/CarrierDialog';
import { RouteKey } from 'broker/enums';
import { useNavigate } from 'broker/hooks';
import CarriersHeader from './CarriersHeader';
import CarriersTableView from './CarriersTableView';
import { ExtendedCarrier } from './types';
import useCarriersFilter, { sorters } from './useCarriersFilter';

export default function CarriersPage() {
  const { page, setPage, rowsPerPage, setRowsPerPage, paginationReset } = usePagination();

  const { items: markets, count, isLoading: isMarketsLoading } = useSearchMarket({});

  const { items: domains, isLoading: isDomainsLoading } = useSearchOrganizationDomain({});

  const extendedCarriers: ExtendedCarrier[] = useMemo(
    () =>
      markets.map((market) => ({
        ...market,
        domains: domains.filter((domain) => domain.organizationId === market.organizationId),
      })),
    [markets, domains],
  );

  const { search, setSearch, sortType, setSortType, sortedAndFilteredItems } = useCarriersFilter(
    extendedCarriers,
    paginationReset,
  );

  const carriersSlice = sortedAndFilteredItems.slice(page * rowsPerPage - rowsPerPage, page * rowsPerPage);
  const isLoading = isMarketsLoading || isDomainsLoading;
  const showItems = extendedCarriers.length > 0 || isLoading;
  const navigate = useNavigate();

  const handleChange = (value: number) => {
    setPage(value);
  };

  const createCarrier = () => {
    navigate(
      {
        pathname: BROKER_NESTED_ROUTES.CARRIERS.CARRIER,
      },
      {
        routeKey: RouteKey.Carrier,
        state: {},
      },
    );
  };

  return (
    <>
      <Stack height={1}>
        <CarriersHeader
          count={count}
          filteredCount={sortedAndFilteredItems.length}
          isFilterApplied={search.length > 1}
          onCreateCarrier={createCarrier}
        />
        <Stack flex={1} p={3} gap={2}>
          {showItems && (
            <Stack direction="row" justifyContent="space-between" maxHeight={40} gap={1} alignItems="center">
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder={messages.carriersPage.search}
                id="search-carriers"
                sx={{ minWidth: 320 }}
              />

              <Stack direction="row" gap={1} justifyContent="flex-end">
                <Sort setSortType={setSortType} sortType={sortType} sorters={sorters} />
              </Stack>
            </Stack>
          )}
          {showItems && (
            <Stack
              flex={1}
              overflow="auto"
              component={Paper}
              variant="outlined"
              divider={
                <Divider
                  sx={
                    sortedAndFilteredItems.length <= rowsPerPage
                      ? { visibility: 'hidden', height: 0, margin: 0, minHeight: 0 }
                      : undefined
                  }
                />
              }
            >
              <CarriersTableView
                carriers={carriersSlice}
                isLoading={isLoading}
                searchInput={search}
                checkedLabels={[]}
              />

              <TablePagination
                from={page * rowsPerPage - rowsPerPage + 1}
                to={page * rowsPerPage}
                outOf={sortedAndFilteredItems.length}
                handleChange={handleChange}
                count={Math.ceil(sortedAndFilteredItems.length / rowsPerPage)}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
              />
            </Stack>
          )}
          {!showItems && (
            <NoItemsFound title={messages.carriersPage.emptyState}>
              <Link component={Button} underline="none" onClick={createCarrier}>
                <Typography variant="h4">{messages.carriersPage.emptyStateSubTitle}</Typography>
              </Link>
            </NoItemsFound>
          )}
        </Stack>
      </Stack>
      <Routes>
        <Route path={BROKER_NESTED_ROUTES.CARRIERS.CARRIER} element={<CarrierDialog />} />
      </Routes>
    </>
  );
}
